import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useCallback, useEffect, useState } from 'react';
import { ThemeColorVariable, ThemeFontSizeVariable, ThemeFontVariable, ThemeSpaceVariable, } from '@caspeco/casper-ui-library.base-ui.theme';
import { Box, Grid, GridItem } from '@chakra-ui/react';
import { addDays, endOfMonth, format, getISOWeek, isSameDay, isSameMonth, startOfMonth, startOfWeek, } from 'date-fns';
import { da, enUS, nb, sv, enGB, nl, fi, es } from 'date-fns/locale';
import { DatePickerDateCell } from './datepicker-date-cell';
import { DatePickerHeader } from './datepicker-header';
import { DatePickerWeekCell } from './datepicker-week-cell';
import { firstLetterUpperCase, getDateCellVariant, getWeekCellVariant, hasDatePassed, hasDotOnDate, isDateStrikeThrough, } from './datepicker.helper';
const localeMap = {
    'en-US': { locale: enUS },
    'en-GB': { locale: enGB },
    'nb-NO': { locale: nb },
    'da-DK': { locale: da },
    'sv-SE': { locale: sv },
    'nl-NL': { locale: nl },
    'fi-FI': { locale: fi },
    'es-ES': { locale: es },
};
export function BaseDatePicker({ selectedDate, onDateChange, onMonthChange, markSundayRed, strikeThroughDates, showDaysOutsideMonth = false, localeString, canSelectPastDates, isExpanded = true, dots, weekNumbers = false, selectWeek = false, shouldDisableDate, selectedMonth: initialMonth, dateRange, isRangeDate = false, onRangeChange, isDisabled = false, redDays = [], disablePastMonths = false, disableBackwardNavigation = false, disableForwardNavigation = false, }) {
    const [selectedMonth, setSelectedMonth] = useState(initialMonth || selectedDate || new Date());
    const [isFirstClick, setIsFirstClick] = useState(true);
    const { locale } = localeMap[localeString];
    useEffect(() => {
        if (selectedDate) {
            setSelectedMonth(initialMonth || selectedDate || new Date());
        }
    }, [isExpanded]);
    const handleMonthChange = (date) => {
        setSelectedMonth(date);
        if (onMonthChange) {
            onMonthChange(date);
        }
    };
    const handleRangeSelection = (date) => {
        // If first click set date to startDate
        if (isFirstClick) {
            if (onRangeChange) {
                onRangeChange({ startDate: date, endDate: null });
            }
            setIsFirstClick(false);
        }
        // If date is less then previous selected startDate set the date as the new startDate
        else if (dateRange?.startDate && dateRange.startDate > date) {
            if (onRangeChange) {
                onRangeChange({ startDate: date, endDate: null });
            }
            setIsFirstClick(false);
        }
        // If its the second click and we have a startDate set the date as endDate
        else {
            if (dateRange?.startDate && isSameDay(date, dateRange.startDate)) {
                return;
            }
            if (dateRange?.startDate && onRangeChange) {
                const newRange = { startDate: dateRange.startDate, endDate: date };
                onRangeChange(newRange);
            }
            setIsFirstClick(true);
        }
    };
    const handleDateClick = useCallback((date) => {
        if (isRangeDate) {
            handleRangeSelection(date);
        }
        else if (onDateChange) {
            onDateChange(date);
        }
    }, [onDateChange, isRangeDate, isFirstClick, handleRangeSelection]);
    function getLocalizedWeekdays() {
        const weekStartDate = startOfWeek(new Date(), { locale });
        return Array.from({ length: 7 }, (_, i) => format(addDays(weekStartDate, i), 'eee', { locale }));
    }
    const month = format(startOfMonth(selectedMonth), 'MMMM yyyy', {
        locale,
    });
    const numDaysInMonth = endOfMonth(selectedMonth).getDate();
    const weekDays = [weekNumbers ? 'v.' : undefined, ...getLocalizedWeekdays()];
    // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    const firstDayOfWeek = locale.options?.weekStartsOn || 0;
    const firstDayOffset = (startOfMonth(selectedMonth).getDay() - firstDayOfWeek + 7) % 7;
    const lastDayOfCurrentMonth = endOfMonth(selectedMonth);
    const lastDayOfCurrentMonthDayOfWeek = lastDayOfCurrentMonth.getDay();
    const daysLeftInWeek = (7 - lastDayOfCurrentMonthDayOfWeek) % 7;
    const responsiveHeight = ['auto', 'auto', '100%'];
    const responsiveWidth = ['auto', 'auto', '100%'];
    const checkIfDateDisabled = (date) => {
        // If the date has passed and we can't select past dates, disable it.
        if (!canSelectPastDates && hasDatePassed(date)) {
            return true;
        }
        // If shouldDisableDate is provided, use it. If not, the date is enabled by default.
        return shouldDisableDate ? shouldDisableDate(date) : false;
    };
    const gridItems = Array.from({ length: numDaysInMonth + firstDayOffset + daysLeftInWeek }, (_, i) => {
        const day = addDays(startOfMonth(selectedMonth), i - firstDayOffset);
        const isInCurrentMonth = isSameMonth(day, selectedMonth);
        const isCurrentDay = isSameDay(day, new Date());
        const isDisabledDay = checkIfDateDisabled(day) || isDisabled;
        const hasYellowDot = hasDotOnDate(day, dots?.yellowDotDates);
        const hasGreenDot = hasDotOnDate(day, dots?.greenDotDates);
        const hasBlueDot = hasDotOnDate(day, dots?.blueDotDates);
        const hasRedDot = hasDotOnDate(day, dots?.redDotDates);
        const hasGreyDot = hasDotOnDate(day, dots?.greyDotDates);
        const isStrikeThrough = isDateStrikeThrough(day, strikeThroughDates) && !isDisabledDay;
        let isSelectedDate = false;
        if (selectedDate) {
            isSelectedDate = isSameDay(day, selectedDate);
        }
        const isSunday = day.getDay() === 0;
        const isHoliday = redDays.some((d) => isSameDay(d, day)) ||
            ((markSundayRed ?? false) && isSunday);
        const weekNumber = getISOWeek(day);
        let isSelectedWeek = false;
        if (selectWeek && selectedDate) {
            isSelectedWeek = getISOWeek(selectedDate) === weekNumber;
        }
        const isOutSideMonth = !isSameMonth(day, selectedMonth);
        const isBetweenRange = Boolean(dateRange?.startDate &&
            dateRange?.endDate &&
            day > dateRange.startDate &&
            day < dateRange.endDate);
        let isStartDate = false;
        if (dateRange?.startDate) {
            isStartDate = isSameDay(day, dateRange?.startDate);
        }
        let isEndDate = false;
        let hasEndDate = false;
        if (dateRange?.endDate) {
            isEndDate = isSameDay(day, dateRange?.endDate);
            hasEndDate = true;
        }
        const variant = getDateCellVariant(isSelectedDate || isSelectedWeek, isCurrentDay, isOutSideMonth, isBetweenRange, isStartDate, isEndDate, hasEndDate);
        const weekNumberVariant = getWeekCellVariant(isSelectedWeek, isCurrentDay);
        const finalVariant = getDateCellVariant(isSelectedDate || isSelectedWeek, isCurrentDay, isOutSideMonth, isBetweenRange, isStartDate, isEndDate);
        const row = Math.floor(i / 7) + 1;
        const column = (i % 7) + 1;
        if (weekNumbers) {
            return (_jsxs(React.Fragment, { children: [weekNumbers && i % 7 === 0 && (_jsx(GridItem, { gridColumn: 1, gridRow: "1 / span 7", opacity: "0.24", borderRight: "1px solid", borderColor: ThemeColorVariable.OnSurfaceBorder }, `grid-${i}-${day}`)), weekNumbers && i % 7 === 0 && (_jsx(DatePickerWeekCell, { handleWeekChange: handleDateClick, date: day, gridIndex: i, variant: weekNumberVariant, selectWeek: selectWeek, isDisabled: isDisabledDay }, `week-${i}-${weekNumber}`)), _jsx(DatePickerDateCell, { hasBlueDot: hasBlueDot, hasGreenDot: hasGreenDot, hasYellowDot: hasYellowDot, hasRedDot: hasRedDot, hasGreyDot: hasGreyDot, gridIndex: i, gridColumn: weekNumbers ? column + 2 : column, gridRow: row, day: day, handleDateChange: handleDateClick, variant: finalVariant, isDisabled: isDisabledDay, isStrikeThrough: isStrikeThrough, selectWeek: selectWeek, isHoliday: isHoliday }, day.toString() + i)] }, `fragment-${i}-${weekNumber}`));
        }
        if (isInCurrentMonth || showDaysOutsideMonth) {
            return (_jsx(DatePickerDateCell, { hasBlueDot: hasBlueDot, hasGreenDot: hasGreenDot, hasYellowDot: hasYellowDot, hasRedDot: hasRedDot, hasGreyDot: hasGreyDot, gridIndex: i, gridColumn: weekNumbers ? column + 1 : column, gridRow: row, day: day, handleDateChange: handleDateClick, variant: variant, isDisabled: isDisabledDay, isStrikeThrough: isStrikeThrough, selectWeek: selectWeek, isHoliday: isHoliday }, `${day.toString()}-${i}`));
        }
        return null;
    });
    return (_jsxs(Box, { w: responsiveWidth, h: responsiveHeight, children: [_jsx(DatePickerHeader, { selectedDate: selectedDate || new Date(), onDateChange: handleDateClick, isExpanded: isExpanded, onMonthChange: handleMonthChange, selectedMonth: selectedMonth, month: month, locale: locale, selectWeek: selectWeek, disablePastMonths: disablePastMonths, disableBackwardNavigation: disableBackwardNavigation, disableForwardNavigation: disableForwardNavigation, dateRange: dateRange, onRangeChange: onRangeChange }), isExpanded && (_jsxs(_Fragment, { children: [_jsx(Grid, { templateColumns: weekNumbers
                            ? '48px 1px 1fr 1fr 1fr 1fr 1fr 1fr 1fr'
                            : 'repeat(7, 1fr)', children: weekDays.map((day, index) => {
                            let gridColumn;
                            if (weekNumbers && index === 0) {
                                gridColumn = 1;
                            }
                            else if (weekNumbers) {
                                gridColumn = index + 2;
                            }
                            else {
                                gridColumn = index;
                            }
                            if (!day)
                                return null;
                            return (_jsx(GridItem, { textAlign: "center", fontWeight: "500", fontFamily: ThemeFontVariable.Body, fontSize: ThemeFontSizeVariable.Small, gridColumn: gridColumn, borderRight: weekNumbers && index === 0
                                    ? `1px solid ${ThemeColorVariable.OnSurfaceBorder}`
                                    : '', pb: ThemeSpaceVariable.Small, children: firstLetterUpperCase(day).slice(0, 2) }, day));
                        }) }), _jsx(Grid, { templateColumns: weekNumbers
                            ? '48px 1px 1fr 1fr 1fr 1fr 1fr 1fr 1fr'
                            : 'repeat(7, 1fr)', rowGap: ThemeSpaceVariable.Small, flexShrink: 1, children: gridItems })] }))] }));
}
//# sourceMappingURL=basedatepicker.js.map